import React, { useState, useEffect, useCallback, useRef, memo, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Polygon, Polyline, Marker, Circle, InfoWindow } from '@react-google-maps/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  MapPin, Package, Clock, CheckCircle, Truck, 
  User, Calendar, Loader2, AlertCircle, Eye, EyeOff, Crosshair
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ['geometry'];

// Central-Time (USA) display formatters. The business runs out of Texas;
// every timestamp shown to clients must be in CT regardless of where the
// viewer's browser happens to be (a tracking link can be opened by anyone,
// anywhere — without this they see whatever timezone their device is in).
const CT_TZ = 'America/Chicago';
const fmtCTDateTime = (iso) => {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    timeZone: CT_TZ,
    month: 'numeric', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
    hour12: true, timeZoneName: 'short',
  });
};
const fmtCTTime = (iso) => {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', {
    timeZone: CT_TZ,
    hour: 'numeric', minute: '2-digit', second: '2-digit',
    hour12: true, timeZoneName: 'short',
  });
};

// Map styling options for Google Maps
const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: true,
  mapTypeControlOptions: { position: 3 },
  streetViewControl: false,
  fullscreenControl: false,
  clickableIcons: false,
  gestureHandling: 'cooperative',
  styles: [
    { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] }
  ]
};

// Normalize GPS points to { lat, lng } object format for Google Maps
const normalizePoint = (point) => {
  if (!point) return null;
  if (Array.isArray(point)) {
    return { lat: point[0], lng: point[1] };
  }
  if (typeof point === 'object') {
    const lat = point.lat ?? point.latitude;
    const lng = point.lng ?? point.lon ?? point.longitude;
    if (lat !== undefined && lng !== undefined) {
      return { lat, lng };
    }
  }
  return null;
};

// Normalize entire route path for Google Maps
const normalizeRoutePath = (routePath) => {
  if (!routePath || !Array.isArray(routePath)) return [];
  return routePath.map(normalizePoint).filter(p => p !== null);
};

// Convert polygon from [[lat, lng], ...] to [{ lat, lng }, ...]
const normalizePolygon = (polygon) => {
  if (!polygon || !Array.isArray(polygon) || polygon.length < 3) return null;
  return polygon.map(p => {
    if (Array.isArray(p)) return { lat: p[0], lng: p[1] };
    return normalizePoint(p);
  }).filter(p => p !== null);
};

// Ray-cast point-in-polygon test. Rings come in as [[lat, lng], ...].
// Used at render time so any house dot / manual delivery mark that
// belongs to a sibling or parent job (legacy data) is hidden when this
// job has an orange polygon — fixes the "deliveries from other jobs"
// leak the customer reported on production.
const _ringContains = (lat, lng, ring) => {
  if (!ring || ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const yi = ring[i][0], xi = ring[i][1];
    const yj = ring[j][0], xj = ring[j][1];
    const intersect = ((yi > lat) !== (yj > lat))
      && (lng < ((xj - xi) * (lat - yi)) / ((yj - yi) || 1e-12) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
};

// Returns true if the job has no usable polygon zone (address-specific /
// canvassing / circle-only). When true, callers should skip the geofence
// because there's nothing to compare against.
const _jobHasNoPolygonZone = (job) => {
  const ring = job?.delivery_boundary;
  const polys = job?.delivery_polygons || [];
  const validRing = Array.isArray(ring) && ring.length >= 3;
  const validPolys = polys.some(p => Array.isArray(p) && p.length >= 3);
  return !validRing && !validPolys;
};

// True if (lat, lng) is inside the job's boundary OR any extra polygon.
// Mirrors the backend `_is_point_inside_job_zone` with the same ~150 m
// tolerance so a front-door dot just past the polygon edge isn't dropped.
const _isPointInsideJobZone = (lat, lng, job, toleranceDeg = 0.0015) => {
  if (lat == null || lng == null) return false;
  if (_jobHasNoPolygonZone(job)) return true; // can't geofence
  const rings = [];
  if (Array.isArray(job?.delivery_boundary) && job.delivery_boundary.length >= 3) {
    rings.push(job.delivery_boundary);
  }
  for (const poly of (job?.delivery_polygons || [])) {
    if (Array.isArray(poly) && poly.length >= 3) rings.push(poly);
  }
  for (const ring of rings) {
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    for (const p of ring) {
      if (p[0] < minLat) minLat = p[0];
      if (p[0] > maxLat) maxLat = p[0];
      if (p[1] < minLng) minLng = p[1];
      if (p[1] > maxLng) maxLng = p[1];
    }
    if (lat < minLat - toleranceDeg || lat > maxLat + toleranceDeg) continue;
    if (lng < minLng - toleranceDeg || lng > maxLng + toleranceDeg) continue;
    if (_ringContains(lat, lng, ring)) return true;
  }
  return false;
};

// Distinct colors for team members' routes
const TEAM_COLORS = ['#06b6d4', '#f97316', '#8b5cf6', '#ec4899', '#14b8a6'];

// Get route color based on session status and team member
const getRouteColor = (session, teamMemberIndex = -1) => {
  if (session.is_previous || session.source === 'previous_session') return '#94a3b8'; // Gray for previous/partial routes
  if (session.user_name && teamMemberIndex >= 0) return TEAM_COLORS[teamMemberIndex % TEAM_COLORS.length];
  if (session.status === 'live') return '#06b6d4'; // Cyan for live
  if (session.status === 'tracking' || session.status === 'in_progress') return '#8b5cf6'; // Purple
  if (session.source === 'history') return '#3b82f6'; // Blue for GPS history
  if (session.source === 'progress') return '#8b5cf6'; // Purple for tracking progress
  if (session.status === 'completed' || session.status === 'approved') return '#22c55e'; // Green
  return '#3b82f6'; // Default blue
};

// Inner map component with auto-fit bounds
const CustomerTrackingMap = memo(({ job, sessions, houseLocations: houseLocationsRaw = [], deliveredHouseIds = [], manualDeliveryLocations: manualDeliveryLocationsRaw = [], showHouseDots = true, deliveryTimestamps = {}, statusHouseInfo = {} }) => {
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [selectedHouse, setSelectedHouse] = useState(null);

  // Geofence every dot to the orange polygon. Without this, parent-job
  // and sibling-split data sneaks onto the customer's tracking map
  // (visible as green/purple dots far outside the drawn zone). When the
  // job has no polygon (address-specific / canvassing) we pass marks
  // through untouched — there's no zone to filter against.
  const houseLocations = useMemo(() => {
    if (_jobHasNoPolygonZone(job)) return houseLocationsRaw;
    return (houseLocationsRaw || []).filter(h => {
      const lat = h?.lat ?? (Array.isArray(h) ? h[0] : null);
      const lng = h?.lng ?? h?.lon ?? (Array.isArray(h) ? h[1] : null);
      if (lat == null || lng == null) return false;
      return _isPointInsideJobZone(Number(lat), Number(lng), job);
    });
  }, [houseLocationsRaw, job]);

  const manualDeliveryLocations = useMemo(() => {
    if (_jobHasNoPolygonZone(job)) return manualDeliveryLocationsRaw;
    return (manualDeliveryLocationsRaw || []).filter(m => {
      let lat = m?.lat;
      let lng = m?.lng;
      if ((lat == null || lng == null) && Array.isArray(m?.position) && m.position.length >= 2) {
        lat = m.position[0];
        lng = m.position[1];
      }
      if (lat == null || lng == null) return false;
      return _isPointInsideJobZone(Number(lat), Number(lng), job);
    });
  }, [manualDeliveryLocationsRaw, job]);

  const useCanvasMarkers = houseLocations.length > 100;

  const { isLoaded } = useJsApiLoader({
    id: 'google-maps-loader',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setMapReady(true);
  }, []);

  // Canvas overlay for large house location sets (>100 markers)
  useEffect(() => {
    if (!mapRef.current || !mapReady || !useCanvasMarkers || !showHouseDots || !window.google) return;
    const map = mapRef.current;
    const deliveredSet = new Set(deliveredHouseIds || []);

    class HouseDotsOverlay extends window.google.maps.OverlayView {
      constructor() { super(); this.canvas = document.createElement('canvas'); this.canvas.style.position = 'absolute'; this.canvas.style.pointerEvents = 'none'; this.canvas.style.zIndex = '999'; }
      onAdd() { this.getPanes().overlayMouseTarget.appendChild(this.canvas); }
      draw() {
        const projection = this.getProjection();
        if (!projection) return;
        const bounds = this.getMap().getBounds();
        if (!bounds) return;
        const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
        const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());
        const width = Math.abs(ne.x - sw.x);
        const height = Math.abs(ne.y - sw.y);
        this.canvas.width = width * 2; this.canvas.height = height * 2;
        this.canvas.style.left = `${sw.x}px`; this.canvas.style.top = `${ne.y}px`;
        this.canvas.style.width = `${width}px`; this.canvas.style.height = `${height}px`;
        const ctx = this.canvas.getContext('2d');
        ctx.scale(2, 2);
        ctx.clearRect(0, 0, width, height);
        for (const house of houseLocations) {
          const lat = house.lat || house[0];
          const lng = house.lon || house.lng || house[1];
          if (!lat || !lng) continue;
          const latLng = new window.google.maps.LatLng(lat, lng);
          if (!bounds.contains(latLng)) continue;
          const pixel = projection.fromLatLngToDivPixel(latLng);
          const x = pixel.x - sw.x;
          const y = pixel.y - ne.y;
          const isDelivered = deliveredSet.has(house.id);
          const flagged = statusHouseInfo[house.id];
          ctx.beginPath(); ctx.arc(x, y, 6, 0, 2 * Math.PI);
          ctx.fillStyle = flagged ? '#7f1d1d' : (isDelivered ? '#fbbf24' : '#22c55e'); ctx.fill();
          ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5; ctx.stroke();
          if (flagged === 'non_deliverable') {
            ctx.beginPath();
            ctx.moveTo(x - 2.5, y - 2.5); ctx.lineTo(x + 2.5, y + 2.5);
            ctx.moveTo(x + 2.5, y - 2.5); ctx.lineTo(x - 2.5, y + 2.5);
            ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5; ctx.stroke();
          }
        }
      }
      onRemove() { if (this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas); }
    }

    const overlay = new HouseDotsOverlay();
    overlay.setMap(map);
    const listeners = [
      map.addListener('zoom_changed', () => overlay.draw()),
      map.addListener('bounds_changed', () => overlay.draw()),
    ];
    return () => { overlay.setMap(null); listeners.forEach(l => window.google.maps.event.removeListener(l)); };
  }, [mapRef.current, mapReady, houseLocations, deliveredHouseIds, statusHouseInfo, useCanvasMarkers, showHouseDots]);

  // Fit bounds to show all relevant data. Auto-runs ONCE when the map is
  // ready — after that only the Recenter button re-frames the view, so
  // polling refreshes and dot clicks never move the map under the user.
  const fitToData = useCallback(() => {
    if (!mapRef.current || !window.google) return false;

    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;

    // Add boundary points
    if (job?.delivery_boundary?.length > 0) {
      job.delivery_boundary.forEach(p => {
        const point = Array.isArray(p) ? { lat: p[0], lng: p[1] } : normalizePoint(p);
        if (point) {
          bounds.extend(point);
          hasPoints = true;
        }
      });
    }

    // Add route points from sessions
    if (sessions?.length > 0) {
      sessions.forEach(session => {
        const routePath = normalizeRoutePath(session.route_path);
        routePath.forEach(point => {
          bounds.extend(point);
          hasPoints = true;
        });
      });
    }

    // Add house/address locations to bounds
    if (houseLocations.length > 0) {
      houseLocations.forEach(h => {
        const lat = h.lat || h[0];
        const lng = h.lon || h.lng || h[1];
        if (lat && lng) {
          bounds.extend({ lat, lng });
          hasPoints = true;
        }
      });
    }

    if (hasPoints) {
      mapRef.current.fitBounds(bounds, { padding: 30 });
    }
    return hasPoints;
  }, [job, sessions, houseLocations]);

  const hasAutoFitRef = useRef(false);
  useEffect(() => {
    if (!mapReady || hasAutoFitRef.current) return;
    if (fitToData()) hasAutoFitRef.current = true;
  }, [mapReady, fitToData]);

  // Stable center reference — a fresh object every render makes the map
  // library re-apply setCenter on ANY re-render (e.g. opening/closing a
  // dot InfoWindow), snapping the view away from where the user panned.
  const mapCenter = useMemo(() => {
    if (job?.map_center) {
      return Array.isArray(job.map_center)
        ? { lat: job.map_center[0], lng: job.map_center[1] }
        : normalizePoint(job.map_center);
    }
    return { lat: 40.7128, lng: -74.0060 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.isArray(job?.map_center) ? job.map_center[0] : job?.map_center?.lat, Array.isArray(job?.map_center) ? job.map_center[1] : job?.map_center?.lng]);

  // Normalize polygon path for Google Maps
  const polygonPath = normalizePolygon(job?.delivery_boundary);

  // Check if circle-based zone (Cars/Hands delivery types)
  const isCircleZone = job?.circle_center && job?.circle_radius_miles;
  const circleCenter = job?.circle_center 
    ? (Array.isArray(job.circle_center) ? { lat: job.circle_center[0], lng: job.circle_center[1] } : normalizePoint(job.circle_center))
    : null;
  const circleRadiusMeters = job?.circle_radius_miles ? job.circle_radius_miles * 1609.34 : 0;

  // Create delivered houses set for quick lookup
  const deliveredHousesSet = new Set(deliveredHouseIds || []);

  if (!isLoaded) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-gray-100">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
    <GoogleMap
      mapContainerStyle={{ height: '100%', width: '100%' }}
      center={mapCenter}
      zoom={14}
      options={mapOptions}
      onLoad={onMapLoad}
    >
      {/* Circle-based delivery zone (for Cars/Hands) */}
      {isCircleZone && circleCenter && (
        <Circle
          center={circleCenter}
          radius={circleRadiusMeters}
          options={{
            fillColor: '#f97316',
            fillOpacity: 0.1,
            strokeColor: '#f97316',
            strokeWeight: 2,
            strokeOpacity: 1
          }}
        />
      )}

      {/* Polygon-based delivery zone boundary */}
      {polygonPath && !isCircleZone && (
        <Polygon
          paths={polygonPath}
          options={{
            fillColor: '#f97316',
            fillOpacity: 0.1,
            strokeColor: '#f97316',
            strokeWeight: 2,
            strokeOpacity: 1
          }}
        />
      )}

      {/* House location markers - canvas for >100, individual Markers for <=100 */}
      {showHouseDots && !useCanvasMarkers && houseLocations && houseLocations.map((house) => {
        const isDelivered = deliveredHousesSet.has(house.id);
        const flagged = statusHouseInfo[house.id];
        const position = { lat: house.lat, lng: house.lon || house.lng };
        
        return (
          <Marker
            key={house.id}
            position={position}
            icon={{
              url: `data:image/svg+xml,${encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                  <circle cx="8" cy="8" r="6" fill="${flagged ? '#7f1d1d' : (isDelivered ? '#fbbf24' : '#22c55e')}" stroke="white" stroke-width="1.5"/>
                  ${flagged === 'non_deliverable' ? '<path d="M5.5 5.5 L10.5 10.5 M10.5 5.5 L5.5 10.5" stroke="white" stroke-width="1.6" stroke-linecap="round"/>' : (!flagged && isDelivered ? '<circle cx="8" cy="8" r="2" fill="#000"/>' : '')}
                </svg>
              `)}`,
              scaledSize: { width: 16, height: 16 },
              anchor: { x: 8, y: 8 }
            }}
            zIndex={flagged ? 65 : (isDelivered ? 60 : 55)}
            onClick={(flagged || isDelivered) ? () => setSelectedHouse({ id: house.id, position, address: house.address, status: flagged }) : undefined}
          />
        );
      })}

      {/* InfoWindow for clicked delivered house */}
      {selectedHouse && (
        <InfoWindow
          position={selectedHouse.position}
          onCloseClick={() => setSelectedHouse(null)}
          options={{ pixelOffset: new window.google.maps.Size(0, -8), disableAutoPan: true }}
        >
          <div style={{ padding: '4px 2px', minWidth: '140px', fontFamily: 'system-ui, sans-serif' }}>
            {selectedHouse.address && (
              <div style={{ fontWeight: 600, fontSize: '12px', color: '#374151', marginBottom: '4px' }}>{selectedHouse.address}</div>
            )}
            <div style={{ fontSize: '11px', color: '#6b7280' }}>
              {selectedHouse.status
                ? `Attempted — ${{ gated: 'Gated Community', non_deliverable: 'Non Deliverable', safety: 'Safety' }[selectedHouse.status] || 'Flagged'}`
                : (deliveryTimestamps[selectedHouse.id]
                  ? `Delivered: ${fmtCTDateTime(deliveryTimestamps[selectedHouse.id])}`
                  : 'Delivered (timestamp unavailable)')}
            </div>
          </div>
        </InfoWindow>
      )}

      {/* GPS route polylines from all sessions */}
      {sessions && (() => {
        // Build a map of user_name -> index for consistent team coloring
        const teamNames = [];
        sessions.forEach(s => {
          if (s.user_name && !teamNames.includes(s.user_name)) teamNames.push(s.user_name);
        });
        
        return sessions.map((session, idx) => {
          const routePath = normalizeRoutePath(session.route_path);
          if (routePath.length < 2) return null;
          const isPrevious = session.is_previous || session.source === 'previous_session';
          const teamIdx = session.user_name ? teamNames.indexOf(session.user_name) : -1;

          return (
            <Polyline
              key={`route-${session.id || idx}-${routePath.length}`}
              path={routePath}
              options={{
                strokeColor: getRouteColor(session, teamIdx),
                strokeWeight: isPrevious ? 3 : (session.status === 'live' ? 6 : 4),
                strokeOpacity: isPrevious ? 0.6 : 0.9,
                ...(isPrevious && {
                  icons: [{
                    icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.6, scale: 3 },
                    offset: '0',
                    repeat: '12px'
                  }],
                  strokeOpacity: 0
                })
              }}
            />
          );
        });
      })()}

      {/* Worker current position marker */}
      {sessions?.length > 0 && (() => {
        const firstSession = sessions[0];
        const currentPos = normalizePoint(firstSession.current_position);
        const routePath = normalizeRoutePath(firstSession.route_path);
        const lastRoutePoint = routePath.length > 0 ? routePath[routePath.length - 1] : null;
        const position = currentPos || lastRoutePoint;

        if (!position) return null;

        return (
          <Marker
            position={position}
            icon={{
              url: `data:image/svg+xml,${encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
                  <circle cx="14" cy="14" r="12" fill="#f97316" stroke="white" stroke-width="3"/>
                  <circle cx="14" cy="14" r="5" fill="white"/>
                </svg>
              `)}`,
              scaledSize: { width: 28, height: 28 },
              anchor: { x: 14, y: 14 }
            }}
            zIndex={100}
            title="Delivery Worker"
          />
        );
      })()}

      {/* Manual delivery locations — rendered in the same amber colour as
          GPS-confirmed deliveries so clients see a single unified
          "delivery" colour. Legacy purple admin marks now match the
          delivered-house dot style. */}
      {showHouseDots && manualDeliveryLocations && manualDeliveryLocations.map((delivery, index) => {
        const position = delivery.position 
          ? { lat: delivery.position[0], lng: delivery.position[1] }
          : delivery.lat && delivery.lng 
            ? { lat: delivery.lat, lng: delivery.lng }
            : null;
        
        if (!position) return null;
        
        return (
          <Marker
            key={delivery.id || `manual-${index}`}
            position={position}
            icon={{
              url: `data:image/svg+xml,${encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="8" fill="#fbbf24" stroke="white" stroke-width="2"/>
                  <circle cx="10" cy="10" r="3" fill="#000"/>
                </svg>
              `)}`,
              scaledSize: { width: 20, height: 20 },
              anchor: { x: 10, y: 10 }
            }}
            zIndex={75}
            title={`Delivery #${index + 1}`}
          />
        );
      })}
    </GoogleMap>
    <button
      type="button"
      data-testid="track-map-recenter-btn"
      onClick={fitToData}
      className="absolute bottom-6 left-4 z-10 bg-white shadow-md rounded-full px-3 py-2 text-xs font-medium text-gray-700 flex items-center gap-1.5 hover:bg-gray-50 border border-gray-200"
    >
      <Crosshair size={14} className="text-orange-500" /> Recenter
    </button>
    </div>
  );
});

CustomerTrackingMap.displayName = 'CustomerTrackingMap';

const CustomerTrackingPage = () => {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [houseLocations, setHouseLocations] = useState([]);
  const [manualDeliveryLocations, setManualDeliveryLocations] = useState([]);
  const [sessionDeliveredHouseIds, setSessionDeliveredHouseIds] = useState([]);
  const [deliveryTimestamps, setDeliveryTimestamps] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [showHouseDots, setShowHouseDots] = useState(true);

  // house_id -> 'non_deliverable' | 'gated' | 'safety' (admin-flagged attempted deliveries)
  const statusHouseInfo = useMemo(() => {
    const m = {};
    (job?.non_deliverable_house_ids || []).forEach((id) => { m[id] = 'non_deliverable'; });
    (job?.gated_house_ids || []).forEach((id) => { m[id] = 'gated'; });
    (job?.safety_house_ids || []).forEach((id) => { m[id] = 'safety'; });
    return m;
  }, [job]);

  const fetchJobData = useCallback(async () => {
    try {
      // Fetch job details (public endpoint)
      const jobResponse = await fetch(`${BACKEND_URL}/api/public/jobs/${jobId}`);
      if (!jobResponse.ok) {
        throw new Error('Job not found');
      }
      const jobData = await jobResponse.json();
      setJob(jobData);

      // Fetch delivery sessions for this job
      const sessionsResponse = await fetch(`${BACKEND_URL}/api/public/jobs/${jobId}/progress`);
      if (sessionsResponse.ok) {
        const sessionsData = await sessionsResponse.json();
        console.log('Fetched sessions:', sessionsData);
        setSessions(sessionsData);
        
        // Extract manual delivery locations, delivered house IDs, and timestamps from sessions
        const allManualLocations = [];
        const allDeliveredHouseIds = [];
        const allTimestamps = {};
        sessionsData.forEach(session => {
          if (session.manual_delivery_locations && Array.isArray(session.manual_delivery_locations)) {
            allManualLocations.push(...session.manual_delivery_locations);
          }
          if (session.delivered_house_ids && Array.isArray(session.delivered_house_ids)) {
            allDeliveredHouseIds.push(...session.delivered_house_ids);
          }
          if (session.delivery_timestamps && typeof session.delivery_timestamps === 'object') {
            Object.assign(allTimestamps, session.delivery_timestamps);
          }
        });
        setManualDeliveryLocations(allManualLocations);
        setSessionDeliveredHouseIds([...new Set(allDeliveredHouseIds)]);
        setDeliveryTimestamps(allTimestamps);
      }

      // Fetch house locations if available (cached from job)
      if (jobData.cached_house_locations) {
        setHouseLocations(jobData.cached_house_locations);
      }

      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Error fetching job:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchJobData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchJobData, 120000);
    return () => clearInterval(interval);
  }, [fetchJobData]);

  const getStatusInfo = (status) => {
    const statusMap = {
      'open': { label: 'Waiting for Assignment', color: 'bg-blue-100 text-blue-700', icon: Clock },
      'in_progress': { label: 'Delivery In Progress', color: 'bg-orange-100 text-orange-700', icon: Truck },
      'completed': { label: 'Delivery Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      'approved': { label: 'Verified & Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      'paid': { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    };
    return statusMap[status] || statusMap['open'];
  };

  const calculateProgress = () => {
    if (!job || !job.deliveries) return 0;
    // Prefer the server-computed canonical total (same math as the client
    // report). Fallback for older cached payloads: max of session sums and
    // the unique delivered-house union.
    const total = (typeof job.delivered_total === 'number')
      ? job.delivered_total
      : Math.max(
          sessions.reduce((sum, s) => sum + (s.deliveries_count || 0), 0),
          new Set([...(job.delivered_house_ids || []), ...sessionDeliveredHouseIds]).size,
        );
    return Math.min(Math.round((total / job.deliveries) * 100), 100);
  };

  // Calculate total GPS route points to show coverage
  const calculateRouteCoverage = () => {
    let totalPoints = 0;
    let hasLiveTracking = false;
    
    sessions.forEach(s => {
      const routePath = normalizeRoutePath(s.route_path);
      if (routePath.length > 0) {
        totalPoints += routePath.length;
      }
      if (s.status === 'live') {
        hasLiveTracking = true;
      }
    });
    
    return { totalPoints, hasLiveTracking };
  };

  const routeCoverage = calculateRouteCoverage();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading delivery status...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Job Not Found</h2>
          <p className="text-gray-500">
            This delivery tracking link may be invalid or expired.
          </p>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusInfo(job.status);
  const StatusIcon = statusInfo.icon;
  const progress = calculateProgress();
  // Server-computed canonical total (identical math to the client report —
  // no double counting between a worker's cumulative tracking doc and their
  // sessions). Fallback for older payloads: max(session sums, house union).
  const totalDelivered = (typeof job.delivered_total === 'number')
    ? job.delivered_total
    : Math.max(
        sessions.reduce((sum, s) => sum + (s.deliveries_count || 0), 0),
        new Set([...(job.delivered_house_ids || []), ...sessionDeliveredHouseIds]).size,
      );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="customer-tracking-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={24} />
            <h1 className="text-2xl font-bold">Delivery Tracking</h1>
          </div>
          <p className="text-orange-100">Real-time delivery progress for your campaign</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Job Info Card */}
        <Card className="p-6 border-0 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{job.title}</h2>
              <p className="text-gray-500">{job.location}</p>
            </div>
            <Badge className={statusInfo.color}>
              <StatusIcon size={14} className="mr-1" />
              {statusInfo.label}
            </Badge>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Delivery Progress</span>
              <span className="font-semibold text-gray-900">{progress}%</span>
            </div>
            <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {totalDelivered.toLocaleString()} of {job.deliveries?.toLocaleString()} deliveries completed
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-orange-50 rounded-xl p-4 text-center">
              <Package size={24} className="mx-auto text-orange-500 mb-2" />
              <p className="text-2xl font-bold text-gray-900">{job.deliveries?.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Total Deliveries</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <CheckCircle size={24} className="mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold text-green-600">{totalDelivered.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Completed</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <Calendar size={24} className="mx-auto text-blue-500 mb-2" />
              <p className="text-sm font-bold text-gray-900">{job.deadline || 'Flexible'}</p>
              <p className="text-xs text-gray-500">Deadline</p>
            </div>
          </div>
        </Card>

        {/* GPS Route Coverage Info */}
        {routeCoverage.totalPoints > 0 && (
          <Card className="border-0 shadow-sm p-4 bg-gradient-to-r from-green-50 to-emerald-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <MapPin size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">GPS Route Recorded</p>
                  <p className="text-sm text-gray-500">
                    {routeCoverage.totalPoints.toLocaleString()} GPS points tracked across the delivery zone
                  </p>
                </div>
              </div>
              {routeCoverage.hasLiveTracking && (
                <Badge className="bg-green-100 text-green-700">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-1 inline-block animate-pulse"></span>
                  Recording
                </Badge>
              )}
            </div>
          </Card>
        )}

        {/* Map */}
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <MapPin size={18} className="text-orange-500" />
              Delivery Zone & GPS Trail
            </h3>
            <div className="flex items-center gap-3">
              {houseLocations.length > 0 && (
                <button
                  onClick={() => setShowHouseDots(prev => !prev)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${showHouseDots ? 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100' : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'}`}
                  data-testid="toggle-house-dots"
                  title={showHouseDots ? 'Hide house dots' : 'Show house dots'}
                >
                  {showHouseDots ? <Eye size={14} /> : <EyeOff size={14} />}
                  House Dots
                </button>
              )}
              {sessions.length > 0 && sessions[0].status === 'live' && (
                <Badge className="bg-orange-100 text-orange-700 animate-pulse">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mr-2 inline-block"></span>
                  Live Tracking
                </Badge>
              )}
            </div>
          </div>
          <div className="h-[600px]">
            {(job.delivery_boundary?.length > 0 || job.circle_center || (job.specific_addresses && job.specific_addresses.length > 0) || houseLocations.length > 0) ? (
              <CustomerTrackingMap
                job={job}
                sessions={sessions}
                houseLocations={houseLocations}
                deliveredHouseIds={[...new Set([...(job.delivered_house_ids || []), ...sessionDeliveredHouseIds])]}
                manualDeliveryLocations={manualDeliveryLocations}
                showHouseDots={showHouseDots}
                deliveryTimestamps={deliveryTimestamps}
                statusHouseInfo={statusHouseInfo}
              />
            ) : (
              <div className="h-full flex items-center justify-center bg-gray-100">
                <p className="text-gray-500">No delivery zone defined</p>
              </div>
            )}
          </div>
          
          {/* Route Legend */}
          {(routeCoverage.totalPoints > 0 || houseLocations.length > 0) && (
            <div className="p-3 bg-gray-50 border-t flex flex-wrap items-center gap-4 text-xs text-gray-600">
              {/* Team member legend */}
              {(() => {
                if (routeCoverage.totalPoints === 0) return null;
                const teamMembers = [];
                sessions.forEach(s => {
                  if (s.user_name && !teamMembers.find(m => m.name === s.user_name)) {
                    teamMembers.push({ name: s.user_name, index: teamMembers.length });
                  }
                });
                if (teamMembers.length > 0) {
                  return teamMembers.map(m => (
                    <span key={m.name} className="flex items-center gap-1">
                      <span className="w-3 h-1 rounded" style={{ backgroundColor: TEAM_COLORS[m.index % TEAM_COLORS.length] }}></span>
                      {m.name}
                    </span>
                  ));
                }
                // Default legend for non-team jobs
                return (
                  <>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-1 bg-cyan-500 rounded"></span> Live Route
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-1 bg-purple-500 rounded"></span> In-Progress
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-1 bg-green-500 rounded"></span> Completed
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-1 bg-blue-500 rounded"></span> Historical GPS
                    </span>
                  </>
                );
              })()}
              {houseLocations.length > 0 && (
                <>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-green-500 rounded-full"></span> House
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-yellow-500 rounded-full"></span> Delivered
                  </span>
                  {Object.values(statusHouseInfo).includes('non_deliverable') && (
                    <span className="flex items-center gap-1" data-testid="legend-non-deliverable">
                      <svg width="12" height="12" viewBox="0 0 12 12">
                        <circle cx="6" cy="6" r="5.5" fill="#7f1d1d" />
                        <path d="M3.8 3.8 L8.2 8.2 M8.2 3.8 L3.8 8.2" stroke="white" strokeWidth="1.4" strokeLinecap="round" />
                      </svg>
                      Non Deliverable
                    </span>
                  )}
                  {Object.values(statusHouseInfo).includes('gated') && (
                    <span className="flex items-center gap-1" data-testid="legend-gated">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#7f1d1d' }}></span> Gated Community
                    </span>
                  )}
                  {Object.values(statusHouseInfo).includes('safety') && (
                    <span className="flex items-center gap-1" data-testid="legend-safety">
                      <svg width="12" height="12" viewBox="0 0 12 12">
                        <circle cx="6" cy="6" r="5.5" fill="#7f1d1d" />
                        <rect x="5.3" y="2.6" width="1.4" height="4.4" rx="0.7" fill="white" />
                        <circle cx="6" cy="9" r="0.9" fill="white" />
                      </svg>
                      Safety
                    </span>
                  )}
                </>
              )}
              {manualDeliveryLocations.length > 0 && (
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-yellow-500 rounded-full"></span> Delivery
                </span>
              )}
            </div>
          )}
        </Card>

        {/* Delivery Sessions */}
        {sessions.length > 0 && (
          <Card className="border-0 shadow-sm">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Truck size={18} className="text-orange-500" />
                Delivery Activity
              </h3>
            </div>
            <div className="divide-y">
              {sessions.map((session, idx) => (
                <div key={session.id || idx} className="p-4 flex items-center justify-between" data-testid={`delivery-session-${idx}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      {session.user_name ? (
                        <User size={20} className="text-green-600" />
                      ) : (
                        <CheckCircle size={20} className="text-green-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {session.user_name ? `${session.user_name}: ` : ''}{session.deliveries_count} deliveries completed
                      </p>
                      <p className="text-sm text-gray-500">
                        {session.created_at ? fmtCTDateTime(session.created_at) : 'Recently'}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-700">
                    {session.status}
                  </Badge>
                </div>
              ))}
            </div>
          </Card>
        )}
        
        {/* No GPS Data Message */}
        {sessions.length === 0 && job.status === 'in_progress' && (
          <Card className="border-0 shadow-sm">
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <MapPin size={24} className="text-yellow-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">GPS Tracking Not Started</h3>
              <p className="text-sm text-gray-500">
                The delivery worker has been assigned but GPS tracking hasn't been started yet. 
                Routes will appear here once tracking begins.
              </p>
            </div>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-gray-400 pb-8">
          <p>Last updated: {fmtCTTime(lastUpdate)}</p>
          <p className="mt-1">Auto-refreshes every 2 minutes</p>
          <p className="mt-4">Powered by Local Flyer Delivery</p>
        </div>
      </div>
    </div>
  );
};

export default CustomerTrackingPage;
